import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, LayoutDashboard, ScrollText, TrendingUp, PenLine, Landmark, FileBadge } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";
import { FinanceDashboard } from "./FinanceDashboard";
import { FinanceLedger } from "./FinanceLedger";
import { FinanceCashflow } from "./FinanceCashflow";
import { FinanceEntries } from "./FinanceEntries";
import { FinanceTax } from "./FinanceTax";
import { OrderFinanceDrawer } from "./OrderFinanceDrawer";
import AdminFinancialOverview from "../AdminFinancialOverview";

const TABS = [
  { id: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { id: "ledger", label: "Ledger", Icon: ScrollText },
  { id: "cashflow", label: "Cashflow", Icon: TrendingUp },
  { id: "entries", label: "Manual entries", Icon: PenLine },
  { id: "tax", label: "Tax & reports", Icon: FileBadge },
  { id: "bank", label: "Bank statements", Icon: Landmark },
];

const nzDate = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
const isoDate = (d) => nzDate(d); // New Zealand calendar day, not UTC

export default function AdminFinance() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const now = useMemo(() => new Date(), []);
  const [filters, setFilters] = useState({ from: `${now.getFullYear()}-01-01`, to: isoDate(now), kind: "", customer: "", payment_status: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    return q.toString();
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/finance/dashboard?${query}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load finance figures."); }
    finally { setLoading(false); }
  }, [query]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setTab = (id) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", id); return n; });
  const changed = () => setRefreshKey((k) => k + 1);

  return (
    <div className="space-y-5 max-w-[1500px]" data-testid="finance-hq-page">
      <header className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ One set of numbers</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3C2A1E] mt-1">Finance HQ</h1>
          <p className="text-sm italic text-[#7A6A55] mt-1 max-w-3xl">Sales, cash, owing, refunds and expenses , all read from the same order records. Nothing is counted twice as an order moves through Packing and Shipping.</p>
          <p className="text-[11px] text-[#9C8B73] mt-1 max-w-3xl" data-testid="finance-scope">Scope: orders in the date range excluding archived, cancelled and priority-review records (Stock Reconciliation counts those too, so its total is higher). Figures are recorded order totals and recorded payments , not a reconciled bank balance. Dates use the New Zealand calendar day.</p>
        </div>
        <nav className="flex flex-wrap gap-1 bg-white border p-1" style={{ borderColor: "#E8DFCB" }} data-testid="finance-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`finance-tab-${id}`}
              className={`h-9 px-3 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors ${tab === id ? "bg-[#3E1F46] text-white" : "text-[#5A4565] hover:bg-[#F3EBDD]"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </nav>
      </header>

      {tab !== "bank" && tab !== "entries" && tab !== "tax" && (
        <FilterBar filters={filters} setFilters={setFilters} kinds={data?.kinds || []} now={now} />
      )}

      {tab === "dashboard" && (loading && !data
        ? <div className="py-20 text-center text-[#6E1E3A]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        : <FinanceDashboard data={data} onOpenOrder={setOrderId} />)}
      {tab === "ledger" && <FinanceLedger query={query} refreshKey={refreshKey} onOpenOrder={setOrderId} />}
      {tab === "cashflow" && <FinanceCashflow filters={filters} refreshKey={refreshKey} />}
      {tab === "entries" && <FinanceEntries onChanged={changed} />}
      {tab === "tax" && <FinanceTax />}
      {tab === "bank" && <AdminFinancialOverview embedded />}

      {orderId && <OrderFinanceDrawer orderId={orderId} onClose={() => setOrderId(null)} onChanged={changed} />}
    </div>
  );
}

function FilterBar({ filters, setFilters, kinds, now }) {
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const input = "h-10 px-3 border text-sm bg-white";
  const label = "block text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E1E3A] mb-1";
  return (
    <div className="flex flex-wrap items-end gap-3 bg-white border p-4" style={{ borderColor: "#E8DFCB" }} data-testid="finance-filters">
      <label className="text-xs"><span className={label}>From</span><input type="date" value={filters.from} onChange={set("from")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="finance-filter-from" /></label>
      <label className="text-xs"><span className={label}>To</span><input type="date" value={filters.to} onChange={set("to")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="finance-filter-to" /></label>
      <label className="text-xs"><span className={label}>Channel</span>
        <select value={filters.kind} onChange={set("kind")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="finance-filter-kind">
          <option value="">All channels</option>
          {kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select></label>
      <label className="text-xs"><span className={label}>Payment</span>
        <select value={filters.payment_status} onChange={set("payment_status")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="finance-filter-status">
          <option value="">Any</option><option value="paid">Paid</option><option value="partial">Part paid</option><option value="unpaid">Unpaid</option>
        </select></label>
      <label className="text-xs flex-1 min-w-[180px]"><span className={label}>Customer / order</span>
        <input value={filters.customer} onChange={set("customer")} placeholder="Name, email or HD number" className={input + " w-full"} style={{ borderColor: "#E8DFCB" }} data-testid="finance-filter-customer" /></label>
      <div className="flex gap-2">
        <button type="button" onClick={() => setFilters((f) => ({ ...f, from: `${now.getFullYear()}-01-01`, to: isoDate(now) }))} className="h-10 px-3 border text-xs font-semibold" style={{ borderColor: "#C9A268" }} data-testid="finance-filter-ytd">Year to date</button>
        <button type="button" onClick={() => setFilters((f) => ({ ...f, from: "", to: "" }))} className="h-10 px-3 border text-xs font-semibold" style={{ borderColor: "#C9A268" }} data-testid="finance-filter-all">All time</button>
        <button type="button" onClick={() => setFilters({ from: `${now.getFullYear()}-01-01`, to: isoDate(now), kind: "", customer: "", payment_status: "" })} className="h-10 px-3 text-xs font-semibold text-[#A33030]" data-testid="finance-filter-reset">Reset</button>
      </div>
    </div>
  );
}
